import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deleteProfileDocument,
  downloadProfileDocument,
  fetchProfile,
  updateProfile,
  uploadProfileDocument,
} from '../../services/profileService';
import { US_STATE_OPTIONS } from '../../data/usStates';
import './Profile.css';

const DOC_TYPE_OPTIONS = [
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'RESUME', label: 'Resume' },
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'DRIVER_LICENSE', label: 'Driver license' },
  { value: 'SSN_CARD', label: 'SSN card' },
  { value: 'OTHER', label: 'Other' },
];

function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function groupDocumentsByType(documents) {
  const grouped = Object.fromEntries(DOC_TYPE_OPTIONS.map((o) => [o.value, []]));
  for (const d of documents || []) {
    if (grouped[d.docType] !== undefined) {
      grouped[d.docType].push(d);
    } else {
      grouped.OTHER.push(d);
    }
  }
  for (const key of Object.keys(grouped)) {
    grouped[key].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
  }
  return grouped;
}

function shortName(name, max = 28) {
  if (!name || name.length <= max) return name || '';
  return `${name.slice(0, max - 1)}…`;
}

/**
 * One row: label | file input | Upload. Optional compact line of existing files below.
 */
function ProfileDocRow({
  docType,
  label,
  documents,
  uploadBusyType,
  setUploadBusyType,
  busyDocId,
  onDownload,
  onDelete,
  onProfileRefresh,
  setError,
  setSuccess,
}) {
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const busy = uploadBusyType === docType;
  const inputId = `pf-file-${docType.toLowerCase()}`;

  const handleUpload = async () => {
    if (!file || busy) return;
    setUploadBusyType(docType);
    setError(null);
    setSuccess(null);
    try {
      await uploadProfileDocument(file, docType);
      await onProfileRefresh();
      setFile(null);
      setFileInputKey((k) => k + 1);
      setSuccess('File uploaded.');
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Upload failed.';
      setError(typeof msg === 'string' ? msg : 'Upload failed.');
    } finally {
      setUploadBusyType(null);
    }
  };

  const actionsLocked = busyDocId !== null || uploadBusyType !== null;

  return (
    <div className="profile-doc-row-wrap">
      <div className="profile-doc-row">
        <span className="profile-doc-row__label" id={`${inputId}-l`}>
          {label}
        </span>
        <div className="profile-doc-row__file">
          <input
            id={inputId}
            key={fileInputKey}
            type="file"
            accept=".pdf,.docx,.jpeg,.jpg,.png"
            aria-labelledby={`${inputId}-l`}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>
        <button
          type="button"
          className="profile-doc-row__upload"
          onClick={handleUpload}
          disabled={!file || busy}
        >
          {busy ? '…' : 'Upload'}
        </button>
      </div>
      {documents.length > 0 ? (
        <div className="profile-doc-row__files" aria-label={`Uploaded for ${label}`}>
          {documents.map((d) => (
            <span key={d.id} className="profile-doc-chip">
              <span className="profile-doc-chip__name" title={d.originalFilename}>
                {shortName(d.originalFilename)} · {formatBytes(d.sizeBytes)}
              </span>
              <button
                type="button"
                className="profile-doc-chip__btn"
                onClick={() => onDownload(d.id)}
                disabled={actionsLocked}
                title="Download"
              >
                ↓
              </button>
              <button
                type="button"
                className="profile-doc-chip__btn profile-doc-chip__btn--del"
                onClick={() => onDelete(d.id)}
                disabled={actionsLocked}
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    thirdPersonSelfIntroduction: '',
    passportNumber: '',
    ssn: '',
  });
  const [uploadBusyType, setUploadBusyType] = useState(null);
  const [busyDocId, setBusyDocId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProfile()
      .then((data) => {
        if (cancelled) return;
        if (data.role === 'ADMIN') {
          navigate('/resume', { replace: true });
          return;
        }
        setProfile(data);
        setForm({
          email: data.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phone: data.phone || '',
          streetAddress: data.streetAddress || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          thirdPersonSelfIntroduction: data.thirdPersonSelfIntroduction || '',
          passportNumber: '',
          ssn: '',
        });
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your profile.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const refreshProfile = useCallback(async () => {
    const next = await fetchProfile();
    setProfile(next);
  }, []);

  const groupedDocuments = useMemo(
    () => groupDocumentsByType(profile?.documents),
    [profile?.documents]
  );

  const onChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setSuccess(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        streetAddress: form.streetAddress.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        thirdPersonSelfIntroduction: form.thirdPersonSelfIntroduction.trim(),
        passportNumber: form.passportNumber.trim(),
        ssn: form.ssn.trim(),
      };
      const next = await updateProfile(payload);
      setProfile(next);
      setSuccess('Profile saved.');
      setForm((f) => ({ ...f, passportNumber: '', ssn: '' }));
      window.dispatchEvent(new Event('uplift-profile-updated'));
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Save failed.';
      setError(typeof msg === 'string' ? msg : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const onDownload = async (id) => {
    setBusyDocId(id);
    setError(null);
    try {
      await downloadProfileDocument(id);
    } catch (err) {
      setError(err.message || 'Download failed.');
    } finally {
      setBusyDocId(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this file from your profile?')) return;
    setBusyDocId(id);
    setError(null);
    setSuccess(null);
    try {
      await deleteProfileDocument(id);
      const next = await fetchProfile();
      setProfile(next);
      setSuccess('File removed.');
    } catch (err) {
      setError(err.message || 'Delete failed.');
    } finally {
      setBusyDocId(null);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <p className="profile-page__lead">Loading profile…</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-page">
        {error && <div className="profile-error">{error}</div>}
        <p className="profile-page__lead">Profile is unavailable.</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <h2>Profile</h2>
      {error && <div className="profile-error">{error}</div>}
      {success && <div className="profile-success">{success}</div>}

      <form onSubmit={onSubmit}>
        <div className="profile-card profile-card--compact">
          <h3>Contact</h3>
          <div className="profile-row profile-row--2">
            <div className="profile-field profile-field--compact">
              <label htmlFor="pf-first">First name</label>
              <input id="pf-first" value={form.firstName} onChange={onChange('firstName')} required />
            </div>
            <div className="profile-field profile-field--compact">
              <label htmlFor="pf-last">Last name</label>
              <input id="pf-last" value={form.lastName} onChange={onChange('lastName')} required />
            </div>
          </div>
          <div className="profile-row profile-row--2">
            <div className="profile-field profile-field--compact">
              <label htmlFor="pf-email">Email</label>
              <input id="pf-email" type="email" value={form.email} onChange={onChange('email')} required />
            </div>
            <div className="profile-field profile-field--compact">
              <label htmlFor="pf-phone">Phone</label>
              <input id="pf-phone" value={form.phone} onChange={onChange('phone')} required />
            </div>
          </div>
        </div>

        <div className="profile-card profile-card--compact">
          <h3>Address</h3>
          <div className="profile-field profile-field--compact profile-field--block">
            <label htmlFor="pf-street">Street</label>
            <textarea
              id="pf-street"
              rows={2}
              value={form.streetAddress}
              onChange={onChange('streetAddress')}
              required
              autoComplete="street-address"
            />
          </div>
          <div className="profile-row profile-row--3">
            <div className="profile-field profile-field--compact">
              <label htmlFor="pf-city">City</label>
              <input id="pf-city" value={form.city} onChange={onChange('city')} required autoComplete="address-level2" />
            </div>
            <div className="profile-field profile-field--compact">
              <label htmlFor="pf-state">State</label>
              <select id="pf-state" value={form.state} onChange={onChange('state')} required autoComplete="address-level1">
                <option value="" disabled>
                  Select
                </option>
                {form.state && !US_STATE_OPTIONS.includes(form.state) ? (
                  <option value={form.state}>{form.state}</option>
                ) : null}
                {US_STATE_OPTIONS.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div className="profile-field profile-field--compact">
              <label htmlFor="pf-zip">ZIP</label>
              <input id="pf-zip" value={form.zip} onChange={onChange('zip')} required autoComplete="postal-code" />
            </div>
          </div>
        </div>

        <div className="profile-card profile-card--compact">
          <h3>Third-party self introduction</h3>
          <div className="profile-field profile-field--compact profile-field--block">
            <label htmlFor="pf-third-person-intro">Introduction</label>
            <textarea
              id="pf-third-person-intro"
              rows={5}
              value={form.thirdPersonSelfIntroduction}
              onChange={onChange('thirdPersonSelfIntroduction')}
              placeholder="Example: Max is a software engineer with..."
            />
          </div>
        </div>

        <div className="profile-card profile-card--compact">
          <h3>Passport &amp; SSN (encrypted)</h3>
          <p className="profile-inline-hint">
            On file: passport {profile.passportLast4 || '—'} · SSN {profile.ssnLast4 || '—'} · blank = keep current
          </p>
          <div className="profile-row profile-row--2">
            <div className="profile-field profile-field--compact">
              <label htmlFor="pf-passport">Passport #</label>
              <input
                id="pf-passport"
                value={form.passportNumber}
                onChange={onChange('passportNumber')}
                autoComplete="off"
                placeholder="New only"
              />
            </div>
            <div className="profile-field profile-field--compact">
              <label htmlFor="pf-ssn">SSN</label>
              <input
                id="pf-ssn"
                value={form.ssn}
                onChange={onChange('ssn')}
                autoComplete="off"
                placeholder="New only"
              />
            </div>
          </div>
        </div>

        <div className="profile-actions profile-actions--compact">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </form>

      <div className="profile-card profile-card--docs-grid">
        <div className="profile-card--docs-grid__head">
          <h3>Documents</h3>
          <p className="profile-docs-hint">Accepted: PDF, DOCX, JPEG, PNG · max 10&nbsp;MB per file</p>
        </div>
        <div className="profile-doc-rows">
          {DOC_TYPE_OPTIONS.map(({ value, label }) => (
            <ProfileDocRow
              key={value}
              docType={value}
              label={label}
              documents={groupedDocuments[value]}
              uploadBusyType={uploadBusyType}
              setUploadBusyType={setUploadBusyType}
              busyDocId={busyDocId}
              onDownload={onDownload}
              onDelete={onDelete}
              onProfileRefresh={refreshProfile}
              setError={setError}
              setSuccess={setSuccess}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
